import {
    ApiRequestDs,
    ApiResponseCodes,
    ApiResponseDs,
    IApiResponseErrorToastContent,
    IApiResponseSuccessToastContent,
    IErrorToastContent,
    IInfoToastContent,
    ISuccessToastContent,
    IToastOptions,
    IWarningToastContent,
    ToastTypes as BizkeytechToastTypes,
    UIUtils as BizkeytechUIUtils
} from "../../@bizkeytech";
import {ToastTypes} from "./type-declerations";
import {type Id as ToastId, toast, ToastContentProps} from "react-toastify";
import {TestToastContent} from "../components/toast-content";
import React, {FunctionComponent} from "react";
import Texts from "../../core/models/texts";
import Utils from "../../core/services/utils";
import ApiEndpoints from "../../core/services/api/endpoints";

/**
 * @inheritDoc
 */
class UIUtils extends BizkeytechUIUtils {

    /**
     * @inheritDoc
     */
    public static get ToastContainerIDs() {
        return {
            ...super.ToastContainerIDs,
        }
    }

    /**
     * Shows toasts associated with the status of the api response if the permission was granted in the request.
     *
     * @param request       The request that was executed.
     * @param response      The response that was received.
     */
    public static showToastApiCallEffect(request: ApiRequestDs, response: ApiResponseDs): void {
        if (!response.resultFlag && !response.aborted && request.showErrorToast) {
            this.toast(
                BizkeytechToastTypes.apiResponseError,
                {
                    title: response?.headerTitle ?? "Error",
                    requestIntent: ApiEndpoints.getIntent(request.url, request.method),
                    message: response?.message ?? ApiResponseCodes.getMessageFromCode(response?.code) ?? Texts.serverError,
                    submitTicket: ![ApiResponseCodes.unauthorized, ApiResponseCodes.internalUnauthorized].includes(response?.code ?? 0),
                    // setting default headers as CURL uses the headers
                    requestModel: response.baseResponse?.config ?? {...request, headers: {}},
                    responseModel: response,
                } as IApiResponseErrorToastContent,
                {toastId: request.url}
            );
        } else {
            super.showToastApiCallEffect(request, response);
        }
    }

    /**
     * Creates a screenshot of the user's browser window and sets it to the response.
     *
     * @param request       The request that was executed.
     * @param response      The response that was received.
     */
    static async captureScreenshotApiCallEffect(request: ApiRequestDs, response: ApiResponseDs): Promise<void> {
        if (response?.resultFlag) {
            return;
        }
        response.viewScreenshot = await Utils.screenshotUserBrowserWindow();
    }

    /**
     * Shows the toast for test toast-type.
     * @param type      the type of the toast that determines the content to be shown.
     * @param data      the data specific for the toast content.
     * @param options   the options related to the toast itself.
     */
    static toast(type: ToastTypes.test, data?: any, options?: IToastOptions): ToastId;
    /**
     * Shows the toast for duplicateCode toast-type.
     * @param type      the type of the toast that determines the content to be shown.
     * @param data      the data specific for the toast content.
     * @param options   the options related to the toast itself.
     */
    static toast(type: BizkeytechToastTypes.apiResponseError, data: IApiResponseErrorToastContent, options?: IToastOptions): ToastId;
    /**
     * Shows the toast for apiResponseSuccess toast-type.
     * @param type      the type of the toast that determines the content to be shown.
     * @param data      the data specific for the toast content.
     * @param options   the options related to the toast itself.
     */
    static toast(type: BizkeytechToastTypes.apiResponseSuccess, data: IApiResponseSuccessToastContent, options?: IToastOptions): ToastId;
    /**
     * Shows the toast for error toast-type.
     * @param type      the type of the toast that determines the content to be shown.
     * @param data      the data specific for the toast content.
     * @param options   the options related to the toast itself.
     */
    static toast(type: BizkeytechToastTypes.error, data: IErrorToastContent, options?: IToastOptions): ToastId;
    /**
     * Shows the toast for info toast-type.
     * @param type      the type of the toast that determines the content to be shown.
     * @param data      the data specific for the toast content.
     * @param options   the options related to the toast itself.
     */
    static toast(type: BizkeytechToastTypes.info, data: IInfoToastContent, options?: IToastOptions): ToastId;
    /**
     * Shows the toast for success toast-type.
     * @param type      the type of the toast that determines the content to be shown.
     * @param data      the data specific for the toast content.
     * @param options   the options related to the toast itself.
     */
    static toast(type: BizkeytechToastTypes.success, data: ISuccessToastContent, options?: IToastOptions): ToastId;
    /**
     * Shows the toast for warning toast-type.
     * @param type      the type of the toast that determines the content to be shown.
     * @param data      the data specific for the toast content.
     * @param options   the options related to the toast itself.
     */
    static toast(type: BizkeytechToastTypes.warning, data: IWarningToastContent, options?: IToastOptions): ToastId;
    /**
     * Shows a toast specific for the given [type].
     * @param type      the type of the toast that determines the content to be shown.
     * @param data      the data specific for the toast content.
     * @param options   the options related to the toast itself.
     */
    static toast(type: BizkeytechToastTypes | ToastTypes, data?: any, options?: IToastOptions): ToastId {
        options = options ?? {};
        let Content: FunctionComponent<ToastContentProps<any>>;
        let containerId;
        switch (type) {
            case ToastTypes.test:
                Content = TestToastContent;
                containerId = this.ToastContainerIDs.default;
                break;
            default:
                return super.toast(type as any, data, options);
        }
        const certainContainerId = options?.containerId ?? containerId ?? this.ToastContainerIDs.default;
        return toast<any>((props) => <Content {...props}/>, {
            ...options,
            containerId: certainContainerId,
            data: data,
        });
    }
}

export * from './type-declerations';
export default UIUtils;
